import { Outlet } from 'react-router-dom'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { BottomNavigation } from '@/components/navigation/BottomNavigation'
import { GlobalHeader } from '@/components/GlobalHeader'
import { useMediaQuery } from '@/hooks/useMediaQuery'
import { useAuthStore } from '@/stores/authStore'
import { PushNotificationsPromptModal } from '@/components/PushNotificationsPromptModal'
import {
  ensurePushNotificationsReady,
  getPushSupportError,
  isPushNotificationsSupported,
  registerForegroundPushListener,
  showTestNotification,
} from '@/lib/pushNotifications'

export function Layout() {
  const isMobile = useMediaQuery('(max-width: 768px)')
  const { t } = useTranslation()
  const { isAuthenticated, user } = useAuthStore()
  const [showPushPrompt, setShowPushPrompt] = useState(false)
  const [pushSupported, setPushSupported] = useState(false)
  const [pushPermission, setPushPermission] = useState<NotificationPermission>('default')
  const [pushProcessing, setPushProcessing] = useState(false)
  const [pushError, setPushError] = useState<string | null>(null)
  const [notificationSettings, setNotificationSettings] = useState<Record<string, any>>({})
  const [hasPrompted, setHasPrompted] = useState(false)

  const shouldCheckPush = useMemo(() => isAuthenticated, [isAuthenticated])

  useEffect(() => {
    setHasPrompted(false)
    setShowPushPrompt(false)
  }, [user?.id])

  useEffect(() => {
    if (showPushPrompt) {
      setPushError(null)
    }
  }, [showPushPrompt])

  useEffect(() => {
    if (!shouldCheckPush) {
      setShowPushPrompt(false)
      return
    }

    const hasNotificationSupport = isPushNotificationsSupported()
    setPushSupported(hasNotificationSupport)
    if (typeof window !== 'undefined' && 'Notification' in window) {
      setPushPermission(Notification.permission)
    }

    if (hasPrompted) {
      return
    }

    const loadSettings = async () => {
      try {
        const response = await fetch('/api/notification-settings/', {
          credentials: 'include',
        })
        const data = await response.json()
        const settings = data.results?.[0] || {}
        setNotificationSettings(settings)

        const pushEnabled = !!settings.push_notifications_enabled
        const permission = hasNotificationSupport ? Notification.permission : 'default'
        const shouldPrompt = permission !== 'granted' || !pushEnabled

        if (shouldPrompt) {
          setShowPushPrompt(true)
          setHasPrompted(true)
        }
      } catch (error) {
        console.error('Error loading notification settings:', error)
      }
    }

    loadSettings()
  }, [shouldCheckPush, hasPrompted])

  useEffect(() => {
    if (!shouldCheckPush) {
      return
    }

    registerForegroundPushListener().catch((error) => {
      console.error('Error registering foreground push listener:', error)
    })
  }, [shouldCheckPush])

  const handleEnablePush = async () => {
    setPushProcessing(true)
    setPushError(null)
    try {
      const supportError = getPushSupportError()
      if (supportError === 'secure_context') {
        setPushError(t('settings.pushPromptSecureContext'))
        return
      }

      if (supportError === 'config_missing') {
        setPushError(t('settings.pushPromptConfigMissing'))
        return
      }

      if (!pushSupported || supportError) {
        setPushError(t('settings.pushPromptUnsupported'))
        return
      }

      const { permission } = await ensurePushNotificationsReady()
      setPushPermission(permission)

      if (permission !== 'granted') {
        setPushError(
          permission === 'denied'
            ? t('settings.pushPromptDenied')
            : t('settings.pushPromptPermissionRequired')
        )
        return
      }

      const payload = {
        ...notificationSettings,
        push_notifications_enabled: true,
        push_transaction_notifications: true,
        push_security_notifications: true,
        push_marketing_notifications: false,
      }

      const response = await fetch('/api/notification-settings/update_settings/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(payload),
      })

      if (!response.ok) {
        throw new Error('notification_settings_update_failed')
      }

      setNotificationSettings(payload)
      setShowPushPrompt(false)
    } catch (error) {
      console.error('Error enabling push notifications:', error)
      const message =
        error instanceof Error && error.message === 'vapid_missing'
          ? t('settings.pushPromptConfigMissing')
          : t('settings.pushPromptEnableError')
      setPushError(message)
    } finally {
      setPushProcessing(false)
    }
  }

  const handleTestPush = () => {
    if (!pushSupported || pushPermission !== 'granted') {
      return
    }
    showTestNotification(t('settings.pushPromptTestTitle'), t('settings.pushPromptTestBody'))
  }

  return (
    <div className="min-h-screen bg-background-light">
      {/* Global Header with Language Selector and Navigation - Always visible */}
      <GlobalHeader showNavigation={!isMobile} />
      
      {/* Main Content */}
      <main className={`${isMobile ? 'pb-20 pt-12' : 'pt-16'}`}>
        <Outlet />
      </main>
      
      {/* Bottom Navigation for Mobile */}
      {isMobile && <BottomNavigation />}

      <PushNotificationsPromptModal
        isOpen={showPushPrompt}
        isSupported={pushSupported}
        permission={pushPermission}
        isProcessing={pushProcessing}
        error={pushError}
        onClose={() => setShowPushPrompt(false)}
        onEnable={handleEnablePush}
        onTest={handleTestPush}
      />
    </div>
  )
}

